import { useParams } from 'react-router-dom';
import WebAppService from '../../../services/WebAppService';
import useActionStore from '../../../store/Actions';
import useProjectsStoreNew from '../../../store/ProjectsNew';
import CustomModalOverlay from '../../common/Custom/CustomModalOverlay/CustomModalOverlay';
import {
  BugFixIcon,
  CleanupIcon,
  FeatureIcon,
  ImprovementIcon,
  InfoIcon,
  RedesignIcon,
} from '../../../svgs/svgs';
import GlobalButton from '../../ui/GlobalButton';
import AssignTask from '../TaskModal/AssignTask/AssignTask';
import { useForm } from '@mantine/form';
import { useState } from 'react';
import PopoverWithType from '../../ui/Popover/PopoverWithType';
import { DeptIcon } from '../../common/Badges/DepartmentBadge/DepartmentBadge';
import SelectInput from '../../ui/SelectInput/SelectInput';
import TextInput from '../../ui/TextInput/TextInput';
import TextArea from '../../ui/TextArea/TextArea';
import { useModal } from '@/context/ModalContext';

export const TASK_TYPE_OPTIONS = [
  { value: 'Bug', label: 'Bug', icon: <BugFixIcon size={16} /> },
  { value: 'Feature', label: 'Feature', icon: <FeatureIcon size={16} /> },
  { value: 'Redesign', label: 'Redesign', icon: <RedesignIcon size={16} /> },

  {
    value: 'Improvement',
    label: 'Improvement',
    icon: <ImprovementIcon size={16} />,
  },
  { value: 'Cleanup', label: 'Cleanup', icon: <CleanupIcon size={16} /> },
];

export default function CreateNewTask() {
  
  const { openModal } = useModal();
  const { updateTasksBoardData: setTasks } = useProjectsStoreNew();
  const { toggleCreateTaskModal, createTaskModal } = useActionStore();
  const { project_id } = useParams();
  const [queuedMember, setQueuedMember] = useState<{
    email: string;
    name: string;
  } | null>(null);

  const handleRefetchBoard = async () => {
    try {
      const res = await WebAppService.getTasksBoard({
        project_id: project_id!,
      });
      setTasks(res);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    try {
      const res = await WebAppService.createTask(form.values, project_id!);
      const newTaskId = res?.data?._id;

      // Assign queued member after task creation
      if (queuedMember && newTaskId) {
        try {
          await WebAppService.assignMemberToTask(newTaskId, {
            user_email: queuedMember.email,
          });
        } catch (error) {
          console.error('Error assigning member to new task:', error);
        }
      }

      handleClose();
      form.reset();
      setQueuedMember(null);
    } catch (error) {
      console.error(error);
    } finally {
      handleRefetchBoard();
    }
  };

  const form = useForm({
    initialValues: {
      title: '',
      description: '',
      task_status: 'To-Do',
      priority: 'P0',
      task_department: 'Design',
      task_type: 'Feature',
      acceptance_criteria: '',
    },
    validate: {
      title: (value: string) =>
        value.trim() ? null : 'Task title is required',
    },
    validateInputOnBlur: true,
  });

  const handleClose = () => {
    toggleCreateTaskModal(false);
    setQueuedMember(null);
  };

  return (
    <CustomModalOverlay
      open={createTaskModal}
      width={'800px'}
      height="fit-content"
      onClose={handleClose}
      title={'Create new task'}
      subTitle={
        'Create and assign a new task to your team in just a few clicks to get things moving.'
      }
      withCloseButton
    >
      {/* ===== Header ======= */}

      <div className="border border-modal-stroke rounded-md w-full  text-sm grid grid-cols-4">
        <div className="w-full p-3 flex flex-col gap-2 border-r border-modal-stroke ">
          <label
            htmlFor="task-assigned"
            className="font-semibold text-input-active-label text-[10px] 2xl:text-xs"
          >
            Task assigned
          </label>

          <div className="">
            <AssignTask deferAssignment onMemberSelect={setQueuedMember} />
          </div>
        </div>

        <div className="w-full p-3 flex flex-col gap-2 border-r border-modal-stroke">
          <label
            htmlFor="task-status"
            className="font-semibold text-input-active-label text-[10px] 2xl:text-xs"
          >
            Task Status
          </label>

          <div className="w-full">
            <PopoverWithType
              size="xs"
              type="status"
              defaultValue={form.values.task_status}
              onChange={(val) => {
                form.setFieldValue('task_status', val);
              }}
            />
          </div>
        </div>

        <div className="w-full p-3 flex flex-col gap-2 border-r border-modal-stroke">
          <div className="w-full flex items-center justify-between gap-2">
            <label
              htmlFor="priority"
              className="font-semibold text-input-active-label text-[10px] 2xl:text-xs"
            >
              Priority
            </label>

            <span
              onClick={() => {
                openModal('priority-levels');
              }}
              className="w-4 h-4 text-input-inactive-placeholder hover:opacity-50 transition-all ease-in-out duration-300 cursor-pointer"
            >
              <InfoIcon />
            </span>
          </div>

          <PopoverWithType
            size="xs"
            type="priority"
            defaultValue={form.values.priority}
            onChange={(val) => {
              form.setFieldValue('priority', val);
            }}
          />
        </div>

        <div className="w-full p-3 flex flex-col gap-2">
          <label
            htmlFor="department"
            className="font-semibold text-input-active-label text-[10px] 2xl:text-xs flex items-center gap-1"
          >
            <div className="w-4 text-input-inactive-placeholder">
              <DeptIcon />
            </div>
            Department
          </label>

          <div className="w-full">
            <PopoverWithType
              size="xs"
              type="department"
              defaultValue={form.values.task_department}
              onChange={(val) => {
                form.setFieldValue('task_department', val);
              }}
            />
          </div>
        </div>
      </div>
      <div className="mt-6">
        <TextInput
          label={'Task title'}
          placeholder={'Enter task title'}
          {...form.getInputProps('title')}
        />
        <div className="mt-4">
          <TextArea
            height="80px"
            label={'Task description'}
            placeholder={'Enter task description'}
            {...form.getInputProps('description')}
            withMaxLength={false}
          />
        </div>
      </div>

      {/* ====== Acceptance criteria ======= */}
      <div className="mt-4">
        <h3 className="text-sm 2xl:text-base text-input-active-label font-semibold">
          Acceptance criteria
        </h3>

        <TextArea
          height="100px"
          withMaxLength={false}
          placeholder={
            'Define measurable conditions to be met for this task to be considered complete.'
          }
          {...form.getInputProps('acceptance_criteria')}
        />
      </div>
      {/* ====== Test cases ======= */}
      <div className="mt-4">
        <h3 className="text-sm 2xl:text-base text-input-active-label font-semibold">
          Test cases
        </h3>

        <TextArea
          height="100px"
          withMaxLength={false}
          placeholder={
            'Document the steps and expected outcomes to verify that this task meets its acceptance criteria.'
          }
          {...form.getInputProps('test_cases')}
        />
      </div>
      <div className="mt-4">
        <SelectInput
          label={'Task type'}
          placeholder="Select task type"
          options={TASK_TYPE_OPTIONS}
          value={form.values.task_type}
          onChange={(val) => {
            form.setFieldValue('task_type', val);
            // handleSave('task_type', val);
          }}
        />
      </div>

      <div className="mt-4 flex items-end justify-end">
        <div>
          <GlobalButton
            state={form.values.title.trim() ? 'default' : 'inactive'}
            onClick={handleSubmit}
          >
            Create New Task
          </GlobalButton>
        </div>
      </div>
    </CustomModalOverlay>
  );
}
